
import React from "react";
import { Box, Heading, Text, VStack, List, ListItem, Divider } from "@chakra-ui/react";

export default function PrivacyPolicy() {
	return (
		<Box maxW="1000px" mx="auto" px={{ base: 4, md: 8 }} py={{ base:24, md: 32 }}>
			<VStack align="stretch" spacing={6}>
				<Heading size="lg">Privacy Policy</Heading>
				<Text color="gray.600">Effective Date: February 3, 2026</Text>

				<Text>
					At Swiftchang, we are dedicated to safeguarding your privacy and personal information. This Privacy Policy describes how we collect, use, and share your personal data, and explains your rights and responsibilities when using our website, mobile application, and services (collectively, "Services").
				</Text>
				<Text>
					This Privacy Policy forms part of our Terms of Use. Any capitalized terms not defined here have the meaning given in the Terms of Use.
				</Text>
				<Divider />

				<VStack align="stretch" spacing={4}>
					<Heading size="md">1. Collection of Personal Information</Heading>
					<Text>
						"Personal Information" refers to any data that identifies or can be linked to a specific individual.
					</Text>
					<Text>We may collect the following types of personal information:</Text>
					<List spacing={2} pl={4} styleType="disc">
						<ListItem>Contact details: name, email address, postal address, and phone number</ListItem>
						<ListItem>Personal details: date of birth, passport number or other identification</ListItem>
						<ListItem>Financial information: bank account number and financial history</ListItem>
						<ListItem>Biometric data: facial scan data from your photo</ListItem>
						<ListItem>Communications: emails and online chat messages</ListItem>
						<ListItem>Online identifiers: IP address, advertising ID, device ID, and other device information</ListItem>
						<ListItem>Source of Funds: information regarding source of funds or wealth, including bank statements</ListItem>
						<ListItem>Other information with your prior consent for provision of our Services</ListItem>
					</List>
					<Text>
						You may choose not to provide certain personal information, but some details are required for us to deliver our Services.
					</Text>
					<Text>
						We also automatically collect technical data through cookies and analytics tools. Cookies enhance your experience and help us improve our website. You can control cookie preferences in your browser settings. Analytics may track usage patterns, features accessed, and performance data, but this information is typically aggregated and not personally identifiable.
					</Text>
					<Text>
						Our Services may contain links to third-party websites. Please note that their privacy policies apply when you visit those sites. We are not responsible for misuse of our Services by users or for risks beyond our control.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">2. Use of Personal Information</Heading>
					  <Text>We may use your personal information for the following purposes:</Text>
					<List spacing={2} pl={4} styleType="disc">
						<ListItem>Providing you with money transfer and wallet services</ListItem>
						<ListItem>Improving usability, performance, and effectiveness of Services</ListItem>
						<ListItem>Establishing identity and protecting from error and fraud</ListItem>
						<ListItem>Troubleshooting and resolving problems</ListItem>
						<ListItem>Sending marketing communications about offers, products, services, and updates</ListItem>
						<ListItem>Monitoring use of Services and enforcing Terms of Use</ListItem>
						<ListItem>Detecting, preventing, or investigating security breaches or fraud</ListItem>
						<ListItem>Complying with legal and regulatory requirements</ListItem>
						<ListItem>As otherwise described at the time of collection</ListItem>
					</List>
					<Text>
						We may also analyze aggregated data to monitor, improve, and enhance our Services. Feedback you provide may be published to help others.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">3. Disclosure and Sharing of Personal Information</Heading>
					<Text>
						We do not share your personal information with third parties without your consent, except as described in this Privacy Policy, to comply with legal obligations, or in cases such as adverse event reporting to regulatory authorities.
					</Text>
					<Text>We may share personal information in the following circumstances:</Text>
					<List spacing={2} pl={4} styleType="disc">
						<ListItem>To affiliates, service providers, and business partners to provide and support Services</ListItem>
						<ListItem>To payment processing and fraud prevention providers</ListItem>
						<ListItem>To data processing, document management, or storage providers</ListItem>
						<ListItem>To marketing, analytics, and digital marketing providers</ListItem>
						<ListItem>To government, law enforcement, or other authorities as required by law</ListItem>
						<ListItem>To a potential buyer of any part of our business, provided the buyer agrees to use Personal Information only for purposes outlined in this Privacy Policy</ListItem>
					</List>
					<Text>
						All third parties are required to comply with applicable privacy laws and to protect your personal information.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">4. Protection and Retention of Personal Information</Heading>
					<Text>
						We use physical, technical, and administrative safeguards to protect your personal information and prevent unauthorized access. Only authorized personnel and suppliers have access, and we take reasonable steps to keep your information accurate and current. However, no method of transmission over the internet is completely secure.
					</Text>
					<Text>
						As a regulated Money Services Business, we may retain personal information for 7-10 years after account closure, as required by law. Information is kept while your account is active and may be retained after termination to comply with legal requirements, prevent fraud, resolve disputes, assist investigations, and troubleshoot issues. Afterward, we may delete or anonymize your data.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">5. Location of Personal Information</Heading>
					<Text>
						Swiftchang is based in Canada. Your personal information may be stored and processed in Canada, the United States, or other countries. We take reasonable steps to ensure your data is protected when transferred internationally. If you access our Services from the EEA, UK, or other regions, your information may be transferred abroad, and we will ensure it receives appropriate safeguards.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">6. Your Rights; Opting Out</Heading>
					<Text>
						You may contact us at any time to request details about your personal information, access or correct your data, or withdraw your consent. We do not sell your information or discriminate against those who opt out. You can unsubscribe from communications at any time. To exercise your rights, email support@swiftchang.com. We may ask you to verify your identity before processing requests.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">7. Children’s Privacy</Heading>
					<Text>
						We do not knowingly collect personal information from children under 18, nor allow them to use or request information through our Services. If you believe a minor has provided us with information, please contact us immediately.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">8. Updates to Privacy Policy</Heading>
					<Text>
						We may update this Privacy Policy at any time to reflect changes in our practices, products, Services, or legal requirements. Updates will be posted on our website. If we make significant changes to how we handle your personal information, we will notify you or seek your consent as appropriate.
					</Text>
				</VStack>

				<VStack align="stretch" spacing={4}>
					<Heading size="md">9. Contact Us</Heading>
					<Text>
						If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at support@swiftchang.com.
					</Text>
				</VStack>
			</VStack>
		</Box>
	);
}
